import { CheckoutObject } from './checkout';
import { Product } from './';
import { CustomerProductPrice } from './price';

export type PunchoutProtocol = 'oci' | 'cxml';

export class PunchoutOrderRow {
  productId: string;
  product: Product;
  quantity: number;
  price: number;
  currency: string;
  customerPrice?: CustomerProductPrice;
}

export type PunchoutPayload =
  | { kind: 'oci-form-post'; fields: { [s: string]: string } }
  | { kind: 'cxml-form-post'; xmlBody: string };

export interface PunchoutCredential {
  domain: string;
  identity: string;
}

/**
 * Credentials extracted from the inbound PunchOutSetupRequest. The response message echoes
 * these credentials back. The standard swaps From and To in the response:
 *   - setup.from   (buyer)    → response.to
 *   - setup.to     (supplier) → response.from / response.sender (Identity only)
 *   - setup.sender (buyer UA) → not echoed; we send our own Sender + UserAgent
 */
export interface PunchoutSetupCredentials {
  from?: PunchoutCredential;
  to?: PunchoutCredential;
  sender?: PunchoutCredential;
}

export interface PunchoutSessionContext {
  launchProtocol: PunchoutProtocol;
  hookUrl: string;
  target: string;
  buyerCookie?: string;
  setupCredentials?: PunchoutSetupCredentials;
}

export interface PunchoutBuildContext {
  session: PunchoutSessionContext;
  currentConfig?: PunchoutCustomerConfig | null;
  checkoutObject?: CheckoutObject;
}

export interface PunchoutAuditMetadata {
  payloadKind?: PunchoutPayload['kind'];
  rowCount?: number;
  total?: number;
  currency?: string;
  failureCode?: string;
}

export interface PunchoutAuditConfigMetadata {
  id: string;
  name: string;
  format: PunchoutProtocol;
  qualifier?: string;
  redirectUrl?: string;
}

export interface PunchoutAuditRecord {
  protocol: PunchoutProtocol;
  launchProtocol: PunchoutProtocol;
  hookUrl: string;
  target: string;
  buyerCookie?: string;
  setupCredentials?: PunchoutSetupCredentials;
  currentConfig?: PunchoutAuditConfigMetadata;
  original?: { checkoutObject: CheckoutObject };
  converted?: PunchoutPayload;
  metadata?: PunchoutAuditMetadata;
  failureCode?: string;
}

/**
 * The type of a stored audit entry. Some entries have no type, because the backend stored them
 * before the field was available. The backend query counts these entries as
 * PUNCHOUT_ORDER_MESSAGE.
 */
export type PunchoutAuditEventType = 'PUNCHOUT_ORDER_MESSAGE' | 'ORDER_REQUEST';

/** Lifecycle of a stored ORDER_REQUEST entry. Only ever set on that event type. */
export type PunchoutOrderIntakeStatus = 'RECEIVED' | 'PROCESSED' | 'FAILED' | 'DUPLICATE';

/**
 * The `converted` map as it comes back out of storage. Shaped like PunchoutPayload, but every
 * field is optional. Entries written by older clients may not match the union type. A failed
 * transfer is stored with no converted payload at all.
 */
export interface PunchoutAuditConvertedPayload {
  kind?: PunchoutPayload['kind'];
  fields?: { [s: string]: string };
  xmlBody?: string;
  [s: string]: unknown;
}

/** A stored punchout audit entry, as returned by GET json/punchout/audit. */
export interface PunchoutAuditLogEntry {
  id: string;
  timestamp?: string;
  type?: PunchoutAuditEventType;
  status?: PunchoutOrderIntakeStatus;
  /** Raw inbound order document, ORDER_REQUEST only. */
  rawDocument?: string;
  payloadId?: string;
  source?: string;
  ediMessageId?: string;
  errorMessage?: string;
  username?: string;
  customerConfigId?: string;
  customerConfigQualifier?: string;
  protocol?: PunchoutProtocol;
  launchProtocol?: PunchoutProtocol;
  hookUrl?: string;
  target?: string;
  buyerCookie?: string;
  /** The configuration at the time of the transfer. It stays available after a deletion. */
  currentConfig?: PunchoutAuditConfigMetadata;
  original?: { checkoutObject?: CheckoutObject; [s: string]: unknown };
  converted?: PunchoutAuditConvertedPayload;
  setupCredentials?: PunchoutSetupCredentials;
  metadata?: PunchoutAuditMetadata;
  /** The backend sets this field on a failed cart transfer only. */
  failureCode?: string;
  extra?: { [s: string]: unknown };
}

/** The date limits of an audit list request. Each value has the format yyyy-MM-dd. */
export interface PunchoutAuditDateRange {
  from?: string;
  to?: string;
}

/**
 * A row of the audit list. The list endpoint gives each entry with all fields and no paging. Thus
 * this type removes the large fields, and a list template cannot show a payload. The detail window
 * gets the full entry by its id.
 *
 * The `rowCount` and `payloadKind` values come from the stored metadata. The `outcome` value has no
 * equivalent field in storage. The list calculates it from the presence of a converted payload.
 */
export type PunchoutAuditListItem = Omit<PunchoutAuditLogEntry, 'rawDocument' | 'original' | 'converted'> & {
  configLabel: string;
  /** True when the entry's config id no longer exists. Use `configLabel` as the fallback name. */
  configMissing?: boolean;
  rowCount?: number;
  payloadKind?: PunchoutPayload['kind'];
  outcome?: PunchoutAuditOutcome;
};

export type PunchoutAuditOutcome = 'OK' | 'FAILED';

export type PunchoutOrderAuthMode = 'NONE' | 'SHARED_SECRET' | 'BASIC_AUTH';

/** An order intake handler registered in the backend, selectable per config. */
export interface PunchoutOrderIntakeHandlerOption {
  id: string;
  label: string;
}

export interface PunchoutCustomerConfig {
  id: string;
  name: string;
  format: PunchoutProtocol;
  qualifier?: string;
  redirectUrl: string;
  orderIntakeEnabled?: boolean;
  orderIntakeHandlerId?: string;
  /**
   * True when the GLN values in an incoming order are copied to the EDI order message. The EDI
   * flow then resolves the customer from the GLN. Backend default is true.
   */
  orderGlnMappingEnabled?: boolean;
  orderAuthMode?: PunchoutOrderAuthMode;
  orderSharedSecret?: string;
  orderBasicAuthUsername?: string;
  orderBasicAuthPassword?: string;
  createdDate?: string;
  updatedDate?: string;
  extra?: { [s: string]: unknown };
}

export class PunchoutError extends Error {
  constructor(
    public code: string,
    message: string
  ) {
    super(message);
    this.name = 'PunchoutError';
  }
}

export const PUNCHOUT_USER_EXTRA_KEY = 'punchoutCustomerConfigId';
